import React, { useState } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';

import { Icon } from 'js/design_system/Icon';
import { Stat } from 'js/design_system/Stat';
import { TextButton } from 'js/design_system/TextButton';
import { Truncate } from 'js/design_system/Truncate';
import { Author } from 'js/liswp/Sidebar/hooks/types';
import formatNumber from 'js/utils/format';

import AuthorCard from './AuthorCard';
import { SidebarSection } from './SidebarSection';
import _StatGroup from "AboutTheAuthorsSection-StatGroup.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/sidebar_ds/AboutTheAuthorsSection.tsx?styleId=StatGroup";
import _ViewAllPapersLink from "AboutTheAuthorsSection-ViewAllPapersLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/sidebar_ds/AboutTheAuthorsSection.tsx?styleId=ViewAllPapersLink";
import _LinkText from "AboutTheAuthorsSection-LinkText.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/sidebar_ds/AboutTheAuthorsSection.tsx?styleId=LinkText";
import _LinkIcon from "AboutTheAuthorsSection-LinkIcon.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/liswp/sidebar_ds/AboutTheAuthorsSection.tsx?styleId=LinkIcon";


// How many authors appear at first, before the "View All" button is clicked
const AUTHORS_LIMIT = 3;

const StatGroup = /*#__PURE__*/styled("div", null, {
  displayName: "StatGroup",
  styles: _StatGroup
});

const ViewAllPapersLink = /*#__PURE__*/styled("a", null, {
  displayName: "ViewAllPapersLink",
  styles: _ViewAllPapersLink
});

const LinkText = /*#__PURE__*/styled("span", null, {
  displayName: "LinkText",
  styles: _LinkText
});

const LinkIcon = /*#__PURE__*/styled(Icon, null, {
  displayName: "LinkIcon",
  styles: _LinkIcon
});

const AuthorStats: React.FC<{
  author: Author;
}> = ({ author }) => {
  if (!author.papers && !author.followers) {
    return null;
  }

  return (
    <StatGroup>
      {author.papers ? (
        <Stat label="Papers" value={formatNumber(author.papers)} />
      ) : null}
      {author.followers ? (
        <Stat label="Followers" value={formatNumber(author.followers)} />
      ) : null}
    </StatGroup>
  );
};

type AboutTheAuthorsProps = {
  authors: Author[];
  onFollow: (author: Author) => void;
  onMessage: (author: Author) => void;
  showViewAllPapersLink?: boolean;
};

const AboutTheAuthors: React.FC<AboutTheAuthorsProps> = ({
  authors,
  onFollow,
  onMessage,
  showViewAllPapersLink = false,
}) => {
  const [limited, setLimited] = useState(true);

  const singleAuthor = authors.length === 1;
  const multipleAuthors = authors.length > 1;
  const hasMoreThanThreeAuthors = authors.length > AUTHORS_LIMIT;

  return (
    <SidebarSection
      heading={`About the ${pluralize('author', authors.length)}`}
      compact={singleAuthor}
    >
      {(limited ? authors.slice(0, AUTHORS_LIMIT) : authors).map((author) => (
        <AuthorCard
          key={author.id}
          author={author}
          onFollow={onFollow}
          onMessage={onMessage}
        />
      ))}

      {singleAuthor && (
        <>
          <Truncate lines={4}>{authors[0].bio}</Truncate>
          <AuthorStats author={authors[0]} />
          {/* Only shows on the LOSWP for now. See AboutTheAuthorsSSR */}
          {showViewAllPapersLink && authors[0].url && (
            <ViewAllPapersLink href={authors[0].url}>
              <LinkText>View all papers from {authors[0].displayName}</LinkText>
              <LinkIcon name="arrow_forward" size={18} />
            </ViewAllPapersLink>
          )}
        </>
      )}

      {multipleAuthors && limited && hasMoreThanThreeAuthors && (
        <TextButton
          inline
          iconRight="keyboard_arrow_down"
          onClick={() => setLimited(false)}
        >
          View all
        </TextButton>
      )}
    </SidebarSection>
  );
};

export default AboutTheAuthors;
